import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, CircularProgress, InputAdornment, TextField } from '@mui/material';
import { Plus, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSidebarConversations } from '../../../components/layout/useSidebarConversations';
import { useNotification } from '../../../hooks/useNotification';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { ProjectCard } from '../components/ProjectCard';
import { ProjectEmptyState } from '../components/ProjectEmptyState';
import { ProjectFormDialog } from '../components/ProjectFormDialog';
import { projectRoute } from '../constants';
import { useMoveConversation } from '../hooks/useMoveConversation';
import { useProjects } from '../hooks/useProjects';
import type { Project } from '../types';

export const ProjectsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { success, error } = useNotification();
  const { projects, isLoaded, enabled, limit, canCreate, createProject, updateProject, deleteProject } = useProjects();
  const { refreshConversations } = useSidebarConversations();
  const moveConversation = useMoveConversation();
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deletingProject, setDeletingProject] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deletingProject) return;
    setIsDeleting(true);
    try {
      const response = await deleteProject(deletingProject.publicId);
      if (response.success) {
        success(t('projects.notifications.deleted'));
        void refreshConversations();
      } else {
        error(response.error || t('projects.notifications.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setDeletingProject(null);
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return projects;
    return projects.filter(
      (project) =>
        project.name.toLowerCase().includes(query) ||
        (project.description ?? '').toLowerCase().includes(query)
    );
  }, [projects, search]);

  const createButton = (
    <Button
      variant="contained"
      size="small"
      startIcon={<Plus size={16} />}
      disabled={!canCreate}
      onClick={() => setIsFormOpen(true)}
    >
      {t('projects.list.newProject')}
    </Button>
  );

  const renderBody = () => {
    if (!isLoaded) {
      return (
        <div className="flex justify-center py-16">
          <CircularProgress size={28} />
        </div>
      );
    }

    if (!enabled) {
      return (
        <ProjectEmptyState
          title={t('projects.list.notInPackageTitle')}
          description={t('projects.list.notInPackageDescription')}
          action={
            <Button component={Link} to="/subscription" variant="contained" size="small">
              {t('projects.list.upgrade')}
            </Button>
          }
        />
      );
    }

    if (projects.length === 0) {
      return (
        <ProjectEmptyState
          title={t('projects.list.emptyTitle')}
          description={t('projects.list.emptyDescription')}
          action={createButton}
        />
      );
    }

    if (filtered.length === 0) {
      return <ProjectEmptyState title={t('projects.list.noResults')} />;
    }

    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {filtered.map((project) => (
          <ProjectCard
            key={project.publicId}
            project={project}
            onDropConversation={(sessionId, target) => void moveConversation(sessionId, target)}
            onEdit={setEditingProject}
            onDelete={setDeletingProject}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="ambient-surface h-full bg-slate-50 p-4 dark:bg-slate-900 sm:p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">
              {t('projects.list.title')}
            </h1>
            <p className="text-sm text-gray-600 dark:text-slate-400">
              {t('projects.list.subtitle')}
              {enabled && limit !== null && (
                <span className="ml-2 font-medium">
                  {t('projects.list.usage', { count: projects.length, limit })}
                </span>
              )}
            </p>
          </div>
          {enabled && createButton}
        </div>

        {enabled && projects.length > 0 && (
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('projects.list.searchPlaceholder')}
            className="mb-4"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />
        )}

        {renderBody()}
      </div>

      <ProjectFormDialog
        open={isFormOpen}
        onSubmit={createProject}
        onClose={() => setIsFormOpen(false)}
        onSuccess={(project) => {
          success(t('projects.notifications.created', { name: project.name }));
          navigate(projectRoute(project.publicId));
        }}
      />
      <ProjectFormDialog
        open={Boolean(editingProject)}
        project={editingProject}
        onSubmit={(values) => updateProject(editingProject?.publicId ?? '', values)}
        onClose={() => setEditingProject(null)}
        onSuccess={() => success(t('projects.notifications.updated'))}
      />
      <ConfirmDialog
        open={Boolean(deletingProject)}
        title={t('projects.detail.deleteTitle')}
        message={t('projects.detail.deleteMessage', { name: deletingProject?.name ?? '' })}
        confirmText={t('projects.detail.delete')}
        cancelText={t('common.cancel')}
        confirmColor="error"
        loading={isDeleting}
        onClose={() => setDeletingProject(null)}
        onConfirm={() => void handleDelete()}
      />
    </div>
  );
};
